import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { AnimatePresence, motion } from 'framer-motion';
import { preloadPublicSlides } from '../services/siteContentService';
import { normalizePublicLink, usePublicLocale } from '../routes/publicPaths';
import { normalizeStorageUrl, preloadImage } from '../config/apiBase';
import { getProgramHeroUrl, getProgramHeroAssets } from '../config/programHeroImages';
import { readSlidesCache, writeSlidesCache, slidePreloadSrc, slideWebpSrcSet } from '../config/slidesCache';
import { tProgramTitle } from '../utils/publicI18n';
import { prefetchProgramHero, prefetchProgramProjets } from '../utils/prefetchProgramHero';
import './HeroSlider.css';

import hero1 from '../assets/images/hero/hero-1.jpg';
import hero2 from '../assets/images/hero/hero-2.jpg';
import hero3 from '../assets/images/hero/hero-3.jpg';
import hero4 from '../assets/images/hero/hero-4.jpg';

const extractProgramSlug = (link) => {
  const match = String(link || '').match(/\/programmes\/([^/?#]+)/);
  return match?.[1] || null;
};

const FALLBACK_IMAGES = [hero1, hero2, hero3, hero4];

const DEFAULT_SLIDES = [
  {
    id: 'default-1',
    title: 'أيتامنا',
    subtitle: 'كَهَاتَيْن ✌️ في الجنة',
    description: 'بدعمك، يتحول العطاء إلى أثرٍ يرافق الأيتام في مسيرة حياتهم.',
    cta_text: 'التفاصيل',
    cta_link: '/programmes/kafala',
    image: hero1,
    use_gradient: false,
  },
  {
    id: 'default-2',
    title: 'سقيا',
    subtitle: '"اسقِهم ماءً... يروِك الله أجراً." 💙💧',
    cta_text: 'التفاصيل',
    cta_link: '/programmes/saqya',
    image: hero2,
    use_gradient: false,
  },
  {
    id: 'default-3',
    title: 'ضيوف الرحمن',
    subtitle: 'عمرة مع جمعية صحبة الخير',
    description:
      'تنظم جمعية صحبة الخير رحلات عمرة لفائدة الراغبين في زيارة بيت الله الحرام والمسجد النبوي، مع الحرص على توفير أفضل ظروف الراحة والتنظيم، حتى يعيش المعتمر تجربة إيمانية مميزة في أجواء من السكينة والطمأنينة.',
    cta_text: 'تبرع الآن',
    cta_link: '/programmes',
    image: hero3,
    use_gradient: true,
  },
  {
    id: 'default-4',
    title: 'قوافل تضامنية',
    subtitle: 'قافلة تضامنية تنظمها جمعية صحبة الخير لدعم الأسر المحتاجة في المناطق النائية.',
    cta_text: 'التفاصيل',
    cta_link: '/programmes/caravane',
    image: hero4,
    use_gradient: false,
  },
];

const mapApiSlides = (apiSlides) =>
  apiSlides
    .filter((slide) => slide.is_active !== false)
    .map((slide, index) => ({
      id: slide.id,
      title: slide.title,
      subtitle: slide.subtitle || '',
      description: slide.description || '',
      cta: slide.cta_text || 'Details',
      ctaLink: slide.cta_link || '/programmes',
      image: normalizeStorageUrl(slide.image_url) || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length],
      imageWebp: normalizeStorageUrl(slide.image_url_webp) || null,
      imageWebpSm: normalizeStorageUrl(slide.image_url_webp_sm) || null,
      alt: slide.title,
      useGradient: slide.use_gradient !== false,
    }));

const buildDefaultSlides = () =>
  DEFAULT_SLIDES.map((s, index) => ({
    id: s.id,
    title: s.title,
    subtitle: s.subtitle,
    description: s.description || '',
    cta: s.cta_text,
    ctaLink: s.cta_link,
    image: s.image || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length],
    imageWebp: null,
    imageWebpSm: null,
    alt: s.title,
    useGradient: s.use_gradient !== false,
  }));

const HeroSlider = () => {
  const { t } = useTranslation();
  const locale = usePublicLocale();
  const cachedSlides = readSlidesCache();
  const [slides, setSlides] = useState(() => cachedSlides || buildDefaultSlides());
  const [loadedImages, setLoadedImages] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    slides.slice(0, 2).forEach((slide) => {
      void preloadImage(slidePreloadSrc(slide));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    const applySlides = async (mapped) => {
      if (cancelled || mapped.length === 0) return;
      const lcp = slidePreloadSrc(mapped[0]);
      if (lcp) {
        const ok = await preloadImage(lcp);
        if (!ok && mapped[0].image && mapped[0].image !== lcp) {
          await preloadImage(mapped[0].image);
        }
      }
      if (cancelled) return;
      setSlides(mapped);
      setCurrentIndex(0);
      writeSlidesCache(mapped);
      mapped.slice(1, 2).forEach((slide) => {
        void preloadImage(slidePreloadSrc(slide));
      });
    };

    const loadSlides = async () => {
      try {
        const data = await preloadPublicSlides();
        if (cancelled) return;

        if (Array.isArray(data) && data.length > 0) {
          await applySlides(mapApiSlides(data));
        }
      } catch (error) {
        console.error('Erreur chargement slides:', error);
      }
    };

    loadSlides();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;

    const nextIndex = (currentIndex + 1) % slides.length;
    const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
    void preloadImage(slidePreloadSrc(slides[nextIndex]));
    void preloadImage(slidePreloadSrc(slides[prevIndex]));
  }, [currentIndex, slides]);

  useEffect(() => {
    if (isHovered || slides.length === 0) return;

    const timer = setInterval(() => {
      hasAnimatedRef.current = true;
      setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 6000);

    return () => clearInterval(timer);
  }, [isHovered, slides.length]);

  const markImageLoaded = useCallback((slideId) => {
    setLoadedImages((prev) => (prev[slideId] ? prev : { ...prev, [slideId]: true }));
  }, []);

  const nextSlide = useCallback(() => {
    hasAnimatedRef.current = true;
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    hasAnimatedRef.current = true;
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const currentSlide = slides[currentIndex];
  const useSlideAnimation = hasAnimatedRef.current;
  const displaySlide = currentSlide || DEFAULT_SLIDES[0];
  const rawCta = displaySlide.cta || displaySlide.cta_text || '';
  const displayCta = !rawCta || rawCta === 'التفاصيل' ? t('public.common.details') : rawCta;
  const displayCtaLink = normalizePublicLink(displaySlide.ctaLink || displaySlide.cta_link || '/programmes', locale);
  const ctaProgramSlug = extractProgramSlug(displayCtaLink);
  const ctaHeroAssets = ctaProgramSlug ? getProgramHeroAssets(ctaProgramSlug) : null;
  const ctaLinkState = ctaProgramSlug
    ? {
        heroImage: ctaHeroAssets.image_url || getProgramHeroUrl(ctaProgramSlug),
        heroWebp: ctaHeroAssets.image_url_webp,
        heroWebpSm: ctaHeroAssets.image_url_webp_sm,
      }
    : undefined;
  const firstSlideImage = slidePreloadSrc(slides[0]);
  const firstWebpSrcSet = slideWebpSrcSet(slides[0]);

  return (
    <div
      className="hero-slider"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {firstSlideImage && (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={firstSlideImage}
            imagesrcset={firstWebpSrcSet || undefined}
            imagesizes={firstWebpSrcSet ? '100vw' : undefined}
            type={firstSlideImage.includes('.webp') ? 'image/webp' : undefined}
            fetchPriority="high"
          />
        </Helmet>
      )}

      <div className="hero-slider__stack" aria-hidden={!currentSlide}>
        {slides.map((slide, index) => {
          const isActive = index === currentIndex;
          const isLoaded = loadedImages[slide.id];

          return (
            <div
              key={slide.id}
              className={`hero-slider__media${isActive ? ' is-active' : ''}`}
            >
              <picture>
                {slideWebpSrcSet(slide) ? (
                  <source type="image/webp" srcSet={slideWebpSrcSet(slide)} sizes="100vw" />
                ) : null}
                <img
                  src={slide.image}
                  alt={isActive ? slide.alt : ''}
                  className={isLoaded || isActive ? 'is-loaded' : ''}
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  loading={index <= 1 ? 'eager' : 'lazy'}
                  decoding={index === 0 ? 'sync' : 'async'}
                  onLoad={() => markImageLoaded(slide.id)}
                  onError={(e) => {
                    const fallback = FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
                    const picture = e.currentTarget.parentElement;
                    if (picture?.tagName === 'PICTURE') {
                      picture.querySelectorAll('source').forEach((source) => source.remove());
                    }
                    if (e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                    }
                  }}
                />
              </picture>
              {slide.useGradient && <div className="hero-slider__overlay" />}
            </div>
          );
        })}
      </div>

      {!currentSlide && (
        <div className="hero-slider__media hero-slider__media--fallback">
          <div className="hero-slider__overlay" />
        </div>
      )}

      <div className={`hero-slider__content${currentSlide?.useGradient !== false ? '' : ' hero-slider__content--plain'}`}>
        <div className="hero-slider__panel">
          {currentSlide && (
            <AnimatePresence mode="wait">
              <motion.div
                key={`text-${currentSlide.id}`}
                className="hero-slider__text"
                initial={useSlideAnimation ? { opacity: 0, y: 16 } : false}
                animate={{ opacity: 1, y: 0 }}
                exit={useSlideAnimation ? { opacity: 0, y: -12 } : undefined}
                transition={{ duration: useSlideAnimation ? 0.35 : 0, ease: 'easeOut' }}
              >
                <h1 className="hero-slider__title">{tProgramTitle(t, ctaProgramSlug, currentSlide.title)}</h1>
                {currentSlide.subtitle && (
                  <p className="hero-slider__subtitle">{currentSlide.subtitle}</p>
                )}
                {currentSlide.description && (
                  <p className="hero-slider__description">{currentSlide.description}</p>
                )}
              </motion.div>
            </AnimatePresence>
          )}
          <Link
            to={displayCtaLink}
            state={ctaLinkState}
            className="hero-slider__cta"
            onMouseEnter={() => {
              if (ctaProgramSlug) {
                void prefetchProgramHero(ctaProgramSlug);
                prefetchProgramProjets(ctaProgramSlug);
              }
            }}
            onMouseDown={() => {
              if (ctaProgramSlug) {
                void prefetchProgramHero(ctaProgramSlug);
                prefetchProgramProjets(ctaProgramSlug);
              }
            }}
          >
            {displayCta}
          </Link>
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={prevSlide}
            className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/20 hover:bg-white/35 backdrop-blur-sm text-white flex items-center justify-center transition-colors"
            aria-label={t('public.common.prevSlide')}
          >
            <FaChevronLeft className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={nextSlide}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/20 hover:bg-white/35 backdrop-blur-sm text-white flex items-center justify-center transition-colors"
            aria-label={t('public.common.nextSlide')}
          >
            <FaChevronRight className="w-4 h-4" />
          </button>

          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => {
                  hasAnimatedRef.current = true;
                  setCurrentIndex(index);
                }}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-10 bg-white'
                    : 'w-6 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={t('public.common.goToSlide', { n: index + 1 })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSlider;
